import React, { useMemo } from "react"
import { useEditor, EditorContent } from "@tiptap/react"
import type { JSONContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import Link from "@tiptap/extension-link"
import TextAlign from "@tiptap/extension-text-align"
import TaskList from "@tiptap/extension-task-list"
import TaskItem from "@tiptap/extension-task-item"
import Highlight from "@tiptap/extension-highlight"
import Superscript from "@tiptap/extension-superscript"
import Subscript from "@tiptap/extension-subscript"
import Table from "@tiptap/extension-table"
import TableRow from "@tiptap/extension-table-row"
import TableCell from "@tiptap/extension-table-cell"
import TableHeader from "@tiptap/extension-table-header"
import { MarkButton } from "../tiptap/tiptap-ui/mark-button"
import { UndoRedoButton } from "../tiptap/tiptap-ui/undo-redo-button"
import { LinkButton } from "../tiptap/tiptap-ui/link-button"
// Styles are imported automatically when using the package
import { Copy, Check, RotateCcw } from "lucide-react";
import { Button } from "@strivelabs/ui-core";
import { Marked } from "marked"
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard"

export interface EditorProps {
    content: string
    contentJSON?: JSONContent
    editable?: boolean
    minHeight?: string
    maxHeight?: string
    copyAsMarkdown?: boolean
    onChange?: (content: string) => void
    onChangeJSON?: (json: JSONContent) => void
    showToolbar?: boolean | 'copy-only' // New prop: true shows all controls, 'copy-only' shows only copy, false hides toolbar
    onRegenerate?: () => void // Optional callback for regenerate button
}

const RichTextEditor: React.FC<EditorProps> = ({
    content,
    contentJSON,
    editable = false,
    minHeight = "200px",
    maxHeight,
    copyAsMarkdown = false,
    onChange,
    onChangeJSON,
    showToolbar,
    onRegenerate
}) => {
    const { copied, copy } = useCopyToClipboard();

    const handleCopyToClipboard = () => {
        if (copyAsMarkdown) {
            // Copy the original markdown content
            copy(content);
        } else {
            // Copy the rendered text
            const postContent = document.getElementById('post-content')
            copy(postContent?.innerText || "");
        }
    };

    const htmlContent = useMemo(() => {
        const marked = new Marked({
            gfm: true,
            breaks: true,
        });
        return marked.parse(content || "");
    }, [content]);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3, 4, 5, 6],
                },
                bold: {},
                italic: {},
                strike: {},
                code: {},
                codeBlock: {},
                blockquote: {},
                horizontalRule: {},
                bulletList: {},
                orderedList: {},
                listItem: {},
                hardBreak: {},
                paragraph: {},
            }),
            Underline,
            Link.configure({
                openOnClick: false, // Always false to allow editing in edit mode
                HTMLAttributes: {
                    class: 'text-blue-600 underline hover:text-blue-800 cursor-pointer',
                    target: '_blank',
                    rel: 'noopener noreferrer',
                },
                validate: href => /^https?:\/\//.test(href) || /^mailto:/.test(href) || /^\/(?!\/)/.test(href),
            }),
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            TaskList,
            TaskItem.configure({ nested: true }),
            Highlight.configure({ multicolor: true }),
            Superscript,
            Subscript,
            Table.configure({
                resizable: true,
                HTMLAttributes: {
                    class: 'tiptap-table',
                },
            }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        editorProps: {
            attributes: {
                class:
                    "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none p-4 tiptap-editor-content",
            },
        },
        content: contentJSON ?? htmlContent,
        editable,
        onUpdate: ({ editor }) => {
            if (editable) {
                if (onChange) {
                    onChange(editor.getHTML());
                }
                if (onChangeJSON) {
                    onChangeJSON(editor.getJSON());
                }
            }
        },
    })

    // Update editor content when it changes externally (but not when user is typing)
    React.useEffect(() => {
        if (editor && !editor.isFocused) {
            if (contentJSON) {
                // When using JSON content, compare by reference (caller controls updates)
                editor.commands.setContent(contentJSON, false);
            } else {
                const currentContent = editor.getHTML();
                if (currentContent !== htmlContent) {
                    editor.commands.setContent(htmlContent, false);
                }
            }
        }
    }, [editor, htmlContent, contentJSON])

    // Build dynamic style for min/max height
    const editorStyle = useMemo(() => {
        const styles: React.CSSProperties = {};
        if (minHeight) styles.minHeight = minHeight;
        if (maxHeight) styles.maxHeight = maxHeight;
        return styles;
    }, [minHeight, maxHeight]);

    // Determine if toolbar should be shown
    const shouldShowToolbar = showToolbar !== undefined ? showToolbar !== false : editable;
    const isCopyOnly = showToolbar === 'copy-only';

    return (
        <div className="border rounded-md overflow-hidden bg-white dark:bg-zinc-900 shadow-sm">
            {shouldShowToolbar && <div className="flex flex-wrap items-center gap-2 p-2 border-b bg-gray-50 dark:bg-zinc-800">
                {!isCopyOnly && (
                    <>
                        <MarkButton editor={editor} type="bold" />
                        <MarkButton editor={editor} type="italic" />
                        <MarkButton editor={editor} type="underline" />
                        <MarkButton editor={editor} type="strike" />
                        <LinkButton editor={editor} />
                        <UndoRedoButton editor={editor} action="undo" className="hover:cursor-pointer" />
                        <UndoRedoButton editor={editor} action="redo" className="hover:cursor-pointer" />
                    </>
                )}
                {copied ? <Check className="h-4 w-4" /> : <Copy onClick={handleCopyToClipboard} className="h-4 w-4 hover:cursor-pointer" />}
                {onRegenerate && (
                    <Button
                        onClick={onRegenerate}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-1 ml-auto"
                    >
                        <RotateCcw className="h-3 w-3" />
                        Regenerate
                    </Button>
                )}
            </div>}
            <div style={editorStyle} className="overflow-y-auto">
                <EditorContent editor={editor} id="post-content" />
            </div>
        </div>
    )
}

export default RichTextEditor
